import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  FormControlLabel,
  Switch,
  Divider,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { useCookieConsent } from './CookieConsentContext';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';

const contentUseCase = new ContentUseCase();

export const CookieSettingsPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const { cookiePreferences, setCookieConsent } = useCookieConsent();
  const navigate = useNavigate();
  const lp = useLocalizedPath();
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [languageEnabled, setLanguageEnabled] = useState(false);
  const [themeEnabled, setThemeEnabled] = useState(false);

  // Initialize state from current preferences
  useEffect(() => {
    setAnalyticsEnabled(cookiePreferences.analytics);
    setLanguageEnabled(cookiePreferences.preferences);
    setThemeEnabled(cookiePreferences.theme ?? false);
  }, [cookiePreferences]);

  const handleSaveSettings = () => {
    // Check if user selected anything
    if (analyticsEnabled || languageEnabled || themeEnabled) {
      // User selected at least one option - accept with selected preferences
      setCookieConsent('accepted', {
        preferences: languageEnabled,
        theme: themeEnabled,
        analytics: analyticsEnabled
      });
    } else {
      // User declined everything - store declined state
      setCookieConsent('declined');
    }

    // Redirect to home page after saving
    navigate(lp('/'));
  };

  const cookieBannerContent = contentUseCase.getCookieBanner(language);

  return (
    <Container maxWidth="md" sx={{ py: theme.spacing(6) }}>
      <Typography variant="h2" component="h1" gutterBottom align="center" color="primary" sx={{ mb: theme.spacing(4) }}>
        {language === 'de' ? 'Cookie-Einstellungen' : 'Cookie Settings'}
      </Typography>

      <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 800 }, mx: 'auto', mb: theme.spacing(4) }}>
        {language === 'de'
          ? 'Hier können Sie Ihre Cookie-Einstellungen jederzeit ändern. Ihre Änderungen werden sofort gespeichert und angewendet.'
          : 'Here you can change your cookie settings at any time. Your changes will be saved and applied immediately.'}
      </Typography>

      <Paper sx={{ p: theme.spacing(4), mt: theme.spacing(3) }}>
        <Typography variant="h5" gutterBottom color="primary" sx={{ mb: theme.spacing(2) }}>
          {language === 'de' ? 'Cookie-Einstellungen anpassen' : 'Adjust Cookie Settings'}
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: theme.spacing(3) }}>
          {language === 'de'
            ? 'Aktivieren Sie die Optionen, die Sie zulassen möchten:'
            : 'Enable the options you want to allow:'}
        </Typography>

        <Box sx={{ mb: theme.spacing(3) }}>
          <FormControlLabel
            control={
              <Switch
                checked={languageEnabled}
                onChange={(e) => setLanguageEnabled(e.target.checked)}
              />
            }
            label={language === 'de' ? 'Spracheinstellung' : 'Language Preference'}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: 1 }}>
            {language === 'de'
              ? 'Diese Einstellung speichert Ihre bevorzugte Sprache für zukünftige Besuche.'
              : 'This setting stores your preferred language for future visits.'}
          </Typography>
        </Box>

        <Divider sx={{ my: theme.spacing(2) }} />

        <Box sx={{ mb: theme.spacing(3) }}>
          <FormControlLabel
            control={
              <Switch
                checked={themeEnabled}
                onChange={(e) => setThemeEnabled(e.target.checked)}
              />
            }
            label={language === 'de' ? 'Design-Modus (Hell/Dunkel)' : 'Theme Mode (Light/Dark)'}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: 1 }}>
            {language === 'de'
              ? 'Diese Einstellung speichert Ihre bevorzugte Design-Modus (Hell oder Dunkel) für zukünftige Besuche.'
              : 'This setting stores your preferred theme mode (light or dark) for future visits.'}
          </Typography>
        </Box>

        <Divider sx={{ my: theme.spacing(2) }} />

        <Box sx={{ mb: theme.spacing(3) }}>
          <FormControlLabel
            control={
              <Switch
                checked={analyticsEnabled}
                onChange={(e) => setAnalyticsEnabled(e.target.checked)}
              />
            }
            label={language === 'de' ? 'Analyse-Cookies (Google Analytics)' : 'Analytics Cookies (Google Analytics)'}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: 1 }}>
            {language === 'de'
              ? 'Google Analytics 4 (Google Ireland Limited) wertet aus, welche Seiten aufgerufen werden, damit wir die Website verbessern können. Dabei werden Cookies gesetzt und eine gekürzte IP-Adresse verarbeitet; eine Übermittlung in die USA ist nicht ausgeschlossen. Schalten Sie die Option ab, wird Google Analytics gestoppt und die Cookies werden gelöscht.'
              : 'Google Analytics 4 (Google Ireland Limited) records which pages are opened so we can improve the website. It sets cookies and processes a truncated IP address; transfer to the USA cannot be ruled out. Switching this off stops Google Analytics and deletes its cookies.'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: theme.spacing(2), justifyContent: 'flex-end', mt: theme.spacing(4) }}>
          <Button
            variant="outlined"
            onClick={() => navigate(lp('/'))}
          >
            {language === 'de' ? 'Abbrechen' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSettings}
          >
            {language === 'de' ? 'Speichern & Zurück' : 'Save & Return'}
          </Button>
        </Box>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: theme.spacing(4), textAlign: 'center' }}>
        {language === 'de'
          ? 'Für detaillierte Informationen über Cookies und Datenverarbeitung siehe unsere '
          : 'For detailed information about cookies and data processing, see our '}
        <Button
          component="a"
          href={lp('/cookies')}
          color="primary"
          sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
        >
          {cookieBannerContent.cookiePolicy}
        </Button>
        {language === 'de' ? ' und ' : ' and '}
        <Button
          component="a"
          href={lp('/privacy')}
          color="primary"
          sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
        >
          {cookieBannerContent.privacyPolicy}
        </Button>.
      </Typography>
    </Container>
  );
};

